import * as React from 'react';
import { Button, Chip, Rating, Skeleton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment, Alert, CircularProgress, Snackbar } from "@mui/material";
import { useEffect, useState } from 'react';
import { ApiRequest } from "GlobalFunctions";
import Box from "elements/Box";
import { Link } from 'react-router-dom';
import { CiStethoscope } from "react-icons/ci";
import { IoBandageOutline } from "react-icons/io5";
import { LuMonitorCheck } from "react-icons/lu";
import { LiaUserEditSolid } from "react-icons/lia";
import { FaWhatsapp } from "react-icons/fa";
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';

const FIELD_LABELS: Record<string, string> = {
    rut: 'RUT', phone: 'Teléfono de contacto', about_me: 'Descripción profesional',
    university_degree: 'Título profesional', service: 'Descripción del servicio',
    price: 'Precio por visita', locations: 'Comunas de servicio', skills: 'Procedimientos que realizas',
};

const validateRut = (rut: string): boolean => {
    const clean = rut.replace(/[.\-\s]/g, '').toUpperCase();
    if (!/^\d{7,8}[0-9K]$/.test(clean)) return false;
    const digits = clean.slice(0, -1);
    const dv = clean.slice(-1);
    let sum = 0, factor = 2;
    for (let i = digits.length - 1; i >= 0; i--) {
        sum += parseInt(digits[i]) * factor;
        factor = factor === 7 ? 2 : factor + 1;
    }
    const rem = 11 - (sum % 11);
    const expected = rem === 11 ? '0' : rem === 10 ? 'K' : String(rem);
    return dv === expected;
};

const formatRut = (value: string): string => {
    const clean = value.replace(/[^\dKk]/g, '').toUpperCase();
    if (clean.length <= 1) return clean;
    const dv = clean.slice(-1);
    const digits = clean.slice(0, -1);
    const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${formatted}-${dv}`;
};

const ALL_PROCEDURES: { skill_id: number; label: string }[] = [
    { skill_id: 1,  label: 'Curación simple' },
    { skill_id: 2,  label: 'Curación avanzada' },
    { skill_id: 3,  label: 'Ostomías' },
    { skill_id: 4,  label: 'Terapia VAC' },
    { skill_id: 5,  label: 'Curación simple' },
    { skill_id: 6,  label: 'Retiro de suturas' },
    { skill_id: 7,  label: 'Tratamiento inyectable' },
    { skill_id: 8,  label: 'Vía venosa' },
    { skill_id: 9,  label: 'Sonda urinaria' },
    { skill_id: 10, label: 'Sonda digestiva' },
];

const defaultValues = {
    user_id: null,
    name: '',
    lastname: '',
    email: '',
    password: '',
    role_id: 1,
    phone: '',
    is_active: false,
    deleted: false,
    image: '',
    nurse_id: null,
    university_degree: '',
    service: '',
    price: '',
    region_id: '',
    region: '',
    locations: [],
    skills: [],
    locations_labels: [],
    skills_labels: [],
    advanced_wound_care: false,
    online_advice: false,
    about_me: '',
    badge_id: '',
    badge: '',
    average_rating: 0,
    whatsapp: '',
    plan_id: 0,
    plan_type: 'basico',
    home_location: '',
}

export default function AccountNurses(props: any) {

    const [formValues, setFormValues] = useState(defaultValues);

    // ── incomplete profile modal ──
    const [missingOpen,   setMissingOpen]   = useState(false);
    const [missingFields, setMissingFields] = useState<string[]>([]);
    const [missingSaving, setMissingSaving] = useState(false);
    const [missingError,  setMissingError]  = useState('');
    const [comunas,       setComunas]       = useState<any[]>([]);
    const [skillsList,    setSkillsList]    = useState<any[]>([]);
    const [missingForm,   setMissingForm]   = useState<Record<string, any>>({
        rut: '', phone: '', about_me: '', university_degree: '',
        service: '', price: '', locations: [], skills: [],
    });
    const [pendingFields, setPendingFields] = useState<string[] | null>(null);
    const [dataReady,     setDataReady]     = useState({ comunas: false, skills: false });
    const [successOpen,   setSuccessOpen]   = useState(false);

    const toggleMulti = (field: 'locations' | 'skills', value: number) => {
        const current: number[] = missingForm[field] || [];
        const next = current.includes(value) ? current.filter((v: number) => v !== value) : [...current, value];
        setMissingForm({ ...missingForm, [field]: next });
    };

    const canSubmitMissing = () => {
        for (const f of missingFields) {
            if (f === 'rut' && !validateRut(missingForm.rut)) return false;
            if (f === 'phone' && (missingForm.phone || '').length < 8) return false;
            if (['about_me', 'university_degree', 'service'].includes(f) && !(missingForm[f] || '').trim()) return false;
            if (f === 'price' && !missingForm.price) return false;
            // solo exigir si el picker es visible; si los datos no cargaron, no bloquear
            if (f === 'locations' && comunas.length > 0 && (!missingForm.locations || missingForm.locations.length === 0)) return false;
            if (f === 'skills' && skillsList.length > 0 && (!missingForm.skills || missingForm.skills.length === 0)) return false;
        }
        return true;
    };

    const submitMissing = () => {
        setMissingSaving(true);
        setMissingError('');
        const body: Record<string, any> = {};
        missingFields.forEach(f => { body[f] = missingForm[f]; });
        ApiRequest({
            url: '/nurses/completeProfile',
            method: 'POST',
            body,
            setResponse: (res: any) => {
                setMissingSaving(false);
                if (res.status === 'success') {
                    setMissingOpen(false);
                    setSuccessOpen(true);
                    loadDetails();
                } else {
                    setMissingError(res.message || 'Error al guardar. Intenta de nuevo.');
                }
            },
            onError: () => { setMissingSaving(false); setMissingError('Error de conexión. Intenta de nuevo.'); }
        });
    };

    const loadDetails = async () => {
        await ApiRequest({
            url: '/users/details',
            method: 'GET',
            setResponse: (response: any) => {
                setFormValues(response);
            }
        });
    }

    // Abre el modal sólo cuando comunas Y skills ya cargaron, evitando que los pickers queden ocultos y el botón gris
    useEffect(() => {
        if (dataReady.comunas && dataReady.skills && pendingFields !== null) {
            setMissingFields(pendingFields);
            setMissingOpen(true);
            setPendingFields(null);
        }
    }, [dataReady, pendingFields]);

    useEffect(() => {
        loadDetails();

        ApiRequest({
            url: '/nurses/completionData',
            method: 'GET',
            setResponse: (res: any) => {
                if (res.status === 'success') {
                    const excluded = ['university_degree', 'service', 'price'];
                    const fields = (res.data.missing_fields || []).filter((f: string) => !excluded.includes(f));
                    if (fields.length > 0) setPendingFields(fields);
                }
            }
        });

        ApiRequest({
            url: '/locations/comunas/list',
            method: 'GET',
            setResponse: (res: any) => {
                setComunas(res || []);
                setDataReady(prev => ({ ...prev, comunas: true }));
            }
        });
        ApiRequest({
            url: '/skills/list',
            method: 'GET',
            setResponse: (res: any) => {
                setSkillsList(res.list || []);
                setDataReady(prev => ({ ...prev, skills: true }));
            }
        });
    }, []);

    return (
        <>
        {/* ── Modal perfil incompleto ── */}
        <Dialog open={missingOpen} maxWidth="sm" fullWidth disableEscapeKeyDown onClose={() => {}}>
            <DialogTitle sx={{ fontWeight: 700, color: '#125095' }}>
                Completa tu perfil
            </DialogTitle>
            <DialogContent dividers>
                <p style={{ margin: '0 0 16px', fontSize: 14, color: '#555' }}>
                    Para aparecer en la plataforma necesitamos que completes los siguientes datos:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                    {missingFields.includes('rut') && (
                        <TextField label={FIELD_LABELS.rut + ' *'} value={missingForm.rut} fullWidth
                            inputProps={{ maxLength: 12 }}
                            onChange={e => setMissingForm({ ...missingForm, rut: formatRut(e.target.value) })}
                            error={missingForm.rut !== '' && !validateRut(missingForm.rut)}
                            color={missingForm.rut !== '' && validateRut(missingForm.rut) ? 'success' : undefined}
                            helperText={
                                missingForm.rut === '' ? 'Ej: 12.345.678-9' :
                                validateRut(missingForm.rut) ? '✓ RUT válido' : 'RUT inválido — verifica el dígito verificador'
                            }
                        />
                    )}

                    {missingFields.includes('phone') && (
                        <TextField label={FIELD_LABELS.phone + ' *'} value={missingForm.phone} fullWidth
                            inputProps={{ maxLength: 9 }}
                            InputProps={{ startAdornment: <InputAdornment position="start">+56</InputAdornment> }}
                            onChange={e => setMissingForm({ ...missingForm, phone: e.target.value.replace(/\D/g, '') })}
                            helperText="Ej: 912345678"
                        />
                    )}

                    {missingFields.includes('university_degree') && (
                        <TextField label={FIELD_LABELS.university_degree + ' *'} value={missingForm.university_degree} fullWidth
                            onChange={e => setMissingForm({ ...missingForm, university_degree: e.target.value })}
                            helperText="Ej: Enfermera Universitaria — Universidad de Chile"
                        />
                    )}

                    {missingFields.includes('about_me') && (
                        <TextField label={FIELD_LABELS.about_me + ' *'} value={missingForm.about_me} fullWidth
                            multiline minRows={3}
                            onChange={e => setMissingForm({ ...missingForm, about_me: e.target.value })}
                            helperText="Descripción breve de tu experiencia y especialidad"
                        />
                    )}

                    {missingFields.includes('service') && (
                        <TextField label={FIELD_LABELS.service + ' *'} value={missingForm.service} fullWidth
                            onChange={e => setMissingForm({ ...missingForm, service: e.target.value })}
                            helperText="¿Qué tipo de atención ofreces?"
                        />
                    )}

                    {missingFields.includes('price') && (
                        <TextField label={FIELD_LABELS.price + ' *'} value={missingForm.price} fullWidth
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            onChange={e => setMissingForm({ ...missingForm, price: e.target.value.replace(/\D/g, '') })}
                            helperText="Precio en pesos chilenos por visita"
                        />
                    )}

                    {missingFields.includes('locations') && comunas.length > 0 && (
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 8 }}>
                                {FIELD_LABELS.locations} * <span style={{ fontWeight: 400, color: '#888' }}>(una o más)</span>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 180, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 8, padding: 10 }}>
                                {comunas.map((c: any) => {
                                    const sel = (missingForm.locations || []).includes(c.comuna_id);
                                    return (
                                        <span key={c.comuna_id} onClick={() => toggleMulti('locations', c.comuna_id)}
                                            style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', userSelect: 'none',
                                                background: sel ? '#125095' : '#f0f4f8', color: sel ? '#fff' : '#444',
                                                border: `1px solid ${sel ? '#125095' : '#ddd'}` }}>
                                            {c.comuna}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {missingFields.includes('skills') && skillsList.length > 0 && (
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 8 }}>
                                {FIELD_LABELS.skills} * <span style={{ fontWeight: 400, color: '#888' }}>(uno o más)</span>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, border: '1px solid #ddd', borderRadius: 8, padding: 10 }}>
                                {skillsList.map((s: any) => {
                                    const sel = (missingForm.skills || []).includes(s.skill_id);
                                    return (
                                        <span key={s.skill_id} onClick={() => toggleMulti('skills', s.skill_id)}
                                            style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', userSelect: 'none',
                                                background: sel ? '#125095' : '#f0f4f8', color: sel ? '#fff' : '#444',
                                                border: `1px solid ${sel ? '#125095' : '#ddd'}` }}>
                                            {s.skill}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {missingError && <Alert severity="error">{missingError}</Alert>}
                </div>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button variant="contained" fullWidth size="large"
                    disabled={!canSubmitMissing() || missingSaving}
                    onClick={submitMissing}
                    sx={{ fontWeight: 700 }}>
                    {missingSaving ? <CircularProgress size={22} color="inherit" /> : 'Guardar y continuar'}
                </Button>
            </DialogActions>
        </Dialog>

        <Snackbar
            open={successOpen}
            autoHideDuration={4000}
            onClose={() => setSuccessOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
            <Alert severity="success" onClose={() => setSuccessOpen(false)} sx={{ width: '100%' }}>
                ¡Perfil completado correctamente!
            </Alert>
        </Snackbar>

        <div className="content">
            <div className='account_flex'>
                <div className='account_left'>
                    <Box>
                        <div className='section_flex'>
                            {/* Imagen de perfil */}
                            <div className="user_image">
                                {formValues.image ? (
                                    <img src={formValues.image} alt="Imagen de perfil" style={{ width: '150px', height: '150px' }} />
                                ): 
                                (
                                    <Skeleton variant="rounded" width={200} height={200}/>
                                )}
                            </div>

                            <div className='account_right'>
                                <div className='info_column'>
                                    <h1 className='user_name'>{formValues.name} {formValues.lastname}</h1>
                                    <div className='items_row' style={{ gap: '6px', marginBottom: '4px' }}>
                                        {formValues.plan_id !== 0 && formValues.badge ? <Chip label={formValues.badge} /> : null}
                                        {formValues.advanced_wound_care
                                            ? <Chip label="Especialista en Heridas" icon={<IoBandageOutline />} color="primary" size="small" />
                                            : <Chip label="Enfermera General" size="small" variant="outlined" />
                                        }
                                    </div>
                                    <span className='user_subtitle'>{formValues.university_degree}</span>
                                    <span className='user_subtitle'>{formValues.region}</span>

                                    <Rating 
                                        value={Number(formValues.average_rating) || 0} 
                                        readOnly 
                                        precision={0.5}
                                        icon={<StarRateRoundedIcon fontSize="inherit" />}
                                        emptyIcon={<StarRateRoundedIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                                    />

                                    
                                    <h3 className='service_title'>Servicio Ofrecido</h3>
                                    <div className='service_row'>
                                        <span className='service_label'>{formValues.service}</span>
                                        <span className='service_price'>{formValues.price}</span>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    component = {Link}
                                    to="/cuenta/editar"
                                    startIcon={<LiaUserEditSolid />}
                                >
                                    Editar
                                </Button>
                            </div>
                        </div>

                        <div>
                            <h2 className='titles'><CiStethoscope size={25} /> Acerca de mi</h2>
                            <p>{formValues.about_me}</p>
                        </div>

                        <div>
                            <h2 className='titles'>Prestaciones</h2>
                            <div className='services_row'>
                                {formValues.advanced_wound_care && (
                                    <span className='item_icon'><IoBandageOutline />Cuidado avanzado de heridas</span>
                                )}

                                {formValues.online_advice && (
                                    <span className='item_icon'><LuMonitorCheck />Asesoria Online</span>
                                )}
                            </div>
                        </div>
                        
                        <div>
                            <h2 className='titles'>Comunas de servicio</h2>
                            <div className='items_row'>
                                {formValues.locations_labels.length > 0 && (
                                    formValues.locations_labels.map((location: any, index: any) => (
                                        <Chip label={location} key={index} color="primary" sx={{ color: '#fff' }} />
                                    ))
                                )}
                            </div>
                        </div>

                        {formValues.home_location && (
                            <div>
                                <h2 className='titles'>Mi comuna de residencia</h2>
                                <p style={{ margin: 0, fontSize: 14, color: '#475569' }}>
                                    {formValues.home_location}
                                </p>
                                <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#94a3b8' }}>
                                    Esta información es privada y no se muestra en tu perfil público.
                                </p>
                            </div>
                        )}

                        <div>
                            <h2 className='titles'>Procedimientos</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                {ALL_PROCEDURES
                                    .filter(p => (Array.isArray(formValues.skills) ? formValues.skills : []).map(Number).includes(p.skill_id))
                                    .map(p => {
                                        let treatments: any = (formValues as any).treatments || {};
                                        if (typeof treatments === 'string') {
                                            try { treatments = JSON.parse(treatments); } catch { treatments = {}; }
                                        }
                                        const price = treatments?.[p.skill_id]?.price || treatments?.[String(p.skill_id)]?.price;
                                        return (
                                            <div key={p.skill_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
                                                <span style={{ fontSize: 14, color: '#334155' }}>{p.label}</span>
                                                {price ? (
                                                    <span style={{ fontSize: 14, fontWeight: 700, color: '#135095' }}>
                                                        ${parseInt(price).toLocaleString('es-CL')}
                                                    </span>
                                                ) : (
                                                    <span style={{ fontSize: 13, color: '#94a3b8' }}>A convenir</span>
                                                )}
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        </div>

                        {formValues.advanced_wound_care && formValues.whatsapp && (
                        <div>
                            <h2 className='titles'>WhatsApp</h2>
                            <div className='items_row'>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => window.open(`https://wa.me/+56${(formValues.whatsapp || '').replace(/\D/g, '').replace(/^56/, '')}`, '_blank')}
                                >
                                    <FaWhatsapp size={23} style={{ marginRight: '7px' }} />
                                    WhatsApp
                                </Button>
                            </div>
                        </div>
                        )}
                    </Box>
                    
                </div>

            </div>
        </div>
        </>
    );
}
